import {
  Add as AddIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Collapse,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { UserPreview } from "components/types";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PATH_DASHBOARD } from "routes/paths";
import { useLocales } from "../../locales";
import { InfoIcon } from "theme/overrides/CustomIcons";
import useAsyncEffect from "use-async-effect";
import axios from "../../utils/axios";

import { useAuthContext } from "../../auth/useAuthContext";
import { useSnackbar } from "../../ui/snackbar";

//import { useLocales } from "../../locales";

export const SelectReviewersPage = () => {
  const { managerId, campaignStepId } = useParams<{
    managerId: string;
    campaignStepId: string;
  }>();
  const { translate } = useLocales();
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [showGuidance, setShowGuidance] = useState(false);
  const [managerName, setManagerName] = useState<string>("");
  const [workspaceUsers, setWorkspaceUsers] = useState<UserPreview[]>([]);

  const [feedbackProviders, setFeedbackProviders] = useState<
    (UserPreview | null)[]
  >([]);

  useAsyncEffect(async () => {
    if (managerId) {
      try {
        const { data } = await axios.get(`/users/direct-report/${managerId}`);
        setManagerName(data.fullName);
      } catch (error) {
        console.warn("Failed to fetch manager name", error);
        setManagerName("[Unavailable]");
      }
    }
  }, [managerId]);

  // fetch direct reports
  //const [directReports, setDirectReports] = useState<UserPreview[]>([]);

  useAsyncEffect(async () => {
    if (managerId) {
      //const { data } = await axios.get(`/users/direct-reports/${managerId}`);
      const { data } = await axios.get<UserPreview[]>(
        `/users/direct-reports/${managerId}`
      );
      //setDirectReports(data);
      //setFeedbackProviders([...data, ...Array(10 - data.length).fill(null)]);
      setFeedbackProviders([
        ...data,
        ...Array(Math.max(0, 10 - data.length)).fill(null),
      ]);
    }
  }, [managerId]);

  //console.log("DIRECT REPORTS: ", directReports);

  // Fetch all users from the database
  useAsyncEffect(async () => {
    if (!user) return;
    const { data } = await axios.get<UserPreview[]>(
      `/users/workspace-users/${user.workspaceId}`
    );
    setWorkspaceUsers(data);
  }, [user]);

  //console.log("Workspace user Fetch: counting ... ", workspaceUsers.length);

  const handleRemove = (index: number) => {
    const updated = [...feedbackProviders];
    updated[index] = null;
    setFeedbackProviders(updated);
  };

  const handleSubmit = async () => {
    try {
      const reviewerIds = feedbackProviders.filter(Boolean).map((u) => u?.id);

      await axios.post("/users/reviewers", {
        managerId,
        reviewerIds,
        campaignStepId,
        minReviewers: 5,
        maxReviewers: 10,
        nbReviewers: feedbackProviders.length,
      });
      enqueueSnackbar(
        translate("select_reviewers.submit_success", { name: managerName }) as unknown as string
      );
    } catch (error) {
      console.error("Error submitting reviewers:", error);
      enqueueSnackbar(
        translate("select_reviewers.submit_error", { name: managerName }) as unknown as string
      );
    }
    navigate(PATH_DASHBOARD.general.app); // Navigate back to home page
  };

  const selectedCount = feedbackProviders.filter(Boolean).length;

  return (
    <Box maxWidth="750px" mx="auto" mt={4}>
      <Typography variant="h4" color="secondary" mb={2}>
        {translate("select_reviewers.title") as unknown as string} {managerName || "[Manager]"}
      </Typography>
      {/* Row with instruction text and "i" icon */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="body2" color="text.secondary">
          {translate("select_reviewers.instruction") as unknown as string}
        </Typography>
        <Tooltip
          title={translate("tooltips.feedback_360_info") as unknown as string}
          placement="bottom"
        >
          <IconButton onClick={() => setShowGuidance((prev) => !prev)}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Collapsible box below */}
      <Collapse in={showGuidance}>
        <Box
          sx={{
            backgroundColor: "#f4f6f8",
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {translate("select_reviewers.guidance_title") as unknown as string}
          </Typography>
          <Typography component="div" variant="body2">
            <ul
              style={{ marginLeft: "1rem", paddingLeft: "1rem", marginTop: 0 }}
            >
              <li>{translate("select_reviewers.guidance_1") as unknown as string}</li>
              <li>{translate("select_reviewers.guidance_2") as unknown as string}</li>
              <li>{translate("select_reviewers.guidance_3") as unknown as string}</li>
              <li>
                {translate("select_reviewers.guidance_4") as unknown as string}
              </li>
            </ul>
          </Typography>
        </Box>
      </Collapse>

      <Grid item xs={12}>
        <Box
          sx={{
            background: "white",
            zIndex: 90,
            position: "relative",
            p: 2,
          }}
        >
          {feedbackProviders.map((person, index) => (
            <Autocomplete
              key={`feedback-${index}`}
              options={workspaceUsers.filter(
                (u) => !feedbackProviders.some((fp) => fp?.email === u.email)
              )}
              getOptionLabel={(option) =>
                `${option.fullName} - ${option.email}`
              }
              value={person}
              onChange={(_event, newValue) => {
                const updated = [...feedbackProviders];
                updated[index] = newValue || null;
                setFeedbackProviders(updated);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("select_reviewers.search_placeholder") as unknown as string}
                  fullWidth
                  sx={{
                    mb: 1,
                    color: "secondary",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {person ? (
                          <IconButton onClick={() => handleRemove(index)}>
                            <DeleteForeverIcon />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                    readOnly: false,
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          ))}
        </Box>
      </Grid>

      <Typography
        variant="body2"
        color={
          selectedCount < 5 || selectedCount > 10 ? "error" : "text.secondary"
        }
        mb={2}
      >
        {selectedCount > 10
          ? (translate("select_reviewers.too_many_selected", {
              count: selectedCount,
              excess: selectedCount - 10,
            }) as unknown as string)
          : selectedCount < 5
          ? (translate("select_reviewers.too_few_selected", {
              count: selectedCount,
              needed: 5 - selectedCount,
            }) as unknown as string)
          : (translate("select_reviewers.count_selected", {
              count: selectedCount,
            }) as unknown as string)}
      </Typography>

      <Fab
        sx={{ position: "fixed", bottom: 20, right: 200 }}
        variant="extended"
        disabled={selectedCount < 5 || selectedCount > 10}
        onClick={handleSubmit}
      >
        <AddIcon />
        {translate("buttons.submit") as unknown as string}
      </Fab>
    </Box>
  );
};

export default SelectReviewersPage;
